import { imageDropzonesInit } from "./imageDropzones";
import { formValidation } from "./formValidation";
import { btnUtils, makeToast, serializeForm } from "./utils";
import type { RecDropzoneFiles, RecDropzoneFilesCompressed } from "./types";

/* Buyer Rating Features */
export const buyerRatingInit = () => {
    // Setup functionality for stars interaction
    const listingRatingStars = document.querySelectorAll(
        ".rec-listing-star",
    ) as NodeListOf<HTMLSpanElement>;
    const listingRating = document.getElementById("rec-listing-rating-container");
    const listingRatingInput = document.getElementById(
        "rec-listing-rating-rating",
    ) as HTMLInputElement | null;

    if (listingRating && listingRatingInput) {
        listingRatingStars.forEach((thisRatingStar) => {
            thisRatingStar.addEventListener("click", (e) => {
                const target = e.target as HTMLSpanElement;
                const thisRating = target.classList.contains("rec-listing-star")
                    ? target
                    : target.closest(".rec-listing-star");

                if (thisRating) {
                    const ratingValue = thisRating.getAttribute("data-rating");
                    if (ratingValue !== null) {
                        listingRating.setAttribute("data-rating", ratingValue);
                        listingRatingInput.value = ratingValue;
                    }
                }
            });
        });
    }

    //submit it
    const buyerRatingExec = async (form: HTMLFormElement) => {
        try {
            // TODO: reverse-eng this form to generate a better type. This was originally written in JS.
            let formData: Record<string, any> = serializeForm(form);

            //cleanup of rating value
            formData.rate = parseInt(formData.rate);

            let buyerRatingResponse = await fetch(
                recAppProxyConfig.paths.pathRatingExec.replace(
                    "%lid%",
                    formData.listing_id,
                ),
                {
                    method: "POST",
                    body: JSON.stringify(formData),
                    headers: { "Content-Type": "application/json" },
                },
            );
            const buyerRatingResults = await buyerRatingResponse.json();
            if (!buyerRatingResults.errors) {
                if (recAppProxyConfig.settings.shop?.landing_page_url?.length > 0) {
                    document.location = recAppProxyConfig.settings.shop.landing_page_url;
                } else {
                    const buyerRatingEntry = document.getElementById(
                        "rec-listing-rating-entry",
                    );
                    const buyerRatingComplete = document.getElementById(
                        "rec-listing-rating-complete",
                    );

                    if (buyerRatingEntry && buyerRatingComplete) {
                        buyerRatingEntry.classList.add("rec-hidden");
                        buyerRatingComplete.classList.remove("rec-hidden");
                    } else {
                        console.error(
                            "Required elements for rating entry and completion are missing.",
                        );
                    }
                }
            } else {
                makeToast("toast-ajax-error");
            }
        } catch (error) {
            makeToast("toast-ajax-error");
        }
        btnUtils.reset();
    };

    //listening for form submission
    const buyerRatingForm = document.getElementById("rec-listing-rating-form") as HTMLFormElement | null;
    if (buyerRatingForm) {
        buyerRatingForm.addEventListener("submit", async (e) => {
            e.preventDefault();
            if (formValidation(buyerRatingForm)) {
                const ratingGoButton = document.getElementById("rec-listing-rating-go") as HTMLButtonElement | null;
                if (ratingGoButton) {
                    btnUtils.loadingToggle(ratingGoButton);
                }
                buyerRatingExec(buyerRatingForm);
            }
        });
    }
};

/* Buyer Dispute Features */
export const buyerDisputeInit = (
    dropzoneObjects: Dropzone[],
    recDropzoneFiles: RecDropzoneFiles,
    recDropzoneFilesCompressed: RecDropzoneFilesCompressed,
    recDropzonePreloaded: Record<string, string>) => {
    imageDropzonesInit(
        "#rec-listing-dispute-form",
        false,
        dropzoneObjects,
        recDropzoneFiles,
        recDropzoneFilesCompressed,
        recDropzonePreloaded,
    );

    //submit it
    const buyerDisputeExec = async (form: HTMLFormElement) => {
        try {
            // TODO: reverse-eng this form to generate a better type. This was originally written in JS.
            let formData: Record<string, any> = serializeForm(form);

            //check/append images
            let disputeImageKeys: string[] = [];
            document
                .querySelectorAll("#rec-listing-dispute-form .dz-preview")
                .forEach((thisPreviewElem) => {
                    const imageKey = thisPreviewElem.getAttribute("data-image-key");
                    if (imageKey === null) {
                        console.error("Image key is null");
                        return;
                    }
                    disputeImageKeys.push(imageKey);
                });

            if (disputeImageKeys.length === 0) {
                makeToast("toast-dispute-images");
                btnUtils.reset();
                const imageValidationError = document.getElementById(
                    "rec-dispute-error-images",
                );
                if (imageValidationError) {
                    imageValidationError.style.display = "block";
                }
                return false;
            } else {
                for (let i = 0; i < disputeImageKeys.length; i++) {
                    formData["dispute_image" + (i + 1)] =
                        recDropzoneFiles[disputeImageKeys[i]].dataURL;
                }

                let buyerDisputeResponse = await fetch(
                    recAppProxyConfig.paths.pathDisputeExec.replace(
                        "%lid%",
                        formData.listing_id,
                    ),
                    {
                        method: "POST",
                        body: JSON.stringify(formData),
                        headers: { "Content-Type": "application/json" },
                    },
                );
                const buyerDisputeResults = await buyerDisputeResponse.json();
                if (!buyerDisputeResults.errors) {
                    if (recAppProxyConfig.settings.shop?.landing_page_url?.length > 0) {
                        document.location =
                            recAppProxyConfig.settings.shop.landing_page_url;
                    } else {
                        const buyerDisputeEntry = document.getElementById("rec-listing-dispute-entry");
                        const buyerDisputeComplete = document.getElementById("rec-listing-dispute-complete");

                        if (buyerDisputeEntry && buyerDisputeComplete) {
                            buyerDisputeEntry.classList.add("rec-hidden");
                            buyerDisputeComplete.classList.remove("rec-hidden");
                        } else {
                            console.error("Required elements for dispute entry and completion are missing.");
                        }
                    }
                } else {
                    makeToast("toast-ajax-error");
                }
            }
        } catch (error) {
            console.error("buyerDisputeExec: ", error);
            makeToast("toast-ajax-error");
        }
        btnUtils.reset();
    };

    //listening for form submission
    const buyerDisputeForm = document.getElementById("rec-listing-dispute-form") as HTMLFormElement | null;
    if (buyerDisputeForm) {
        buyerDisputeForm.addEventListener("submit", async (e) => {
            e.preventDefault();
            if (formValidation(buyerDisputeForm)) {
                const disputeGoButton = document.getElementById("rec-listing-dispute-go") as HTMLButtonElement | null;
                if (disputeGoButton) {
                    btnUtils.loadingToggle(disputeGoButton);
                }
                buyerDisputeExec(buyerDisputeForm);
            }
        });
    }
};